import { extractJson } from '../utils/jsonParser.js';

const VALID_ANSWERS = ['yes', 'no', 'unknown', 'irrelevant'];
const WIN_ACCURACY_THRESHOLD = 0.85;

const QUESTION_SYSTEM_PROMPT = `你是一场海龟汤（情境推理谜题）的主持人，你心里知道汤底（隐藏真相），但玩家只看过汤面。

玩家的输入是一个关于汤面的是非问句。你只能根据“汤底核心要素清单”和“汤底”判断。

回答规则：
1. 如果问题可以用“是”或“否”明确回答，回答 yes 或 no。
2. 如果问题无法根据汤底判断，回答 unknown。
3. 如果问题与汤面无关或无法回答，回答 irrelevant。
4. 反馈（feedback）只能给出 1 句简短回应，要像海龟汤主持人一样给出简短提示，不能泄露汤底、关键动作、隐藏真相或核心反转。
5. feedback 中的事实必须与汤面（description）、核心要素（facts）和汤底（fullStory）保持一致，不能说出与汤面矛盾的具体细节。
6. 所有文本使用简体中文（zh-CN）。

返回 ONLY JSON：
{
  "answer": "yes|no|unknown|irrelevant",
  "feedback": "string",
  "reason": "string"
}`;

const RECONSTRUCTION_SYSTEM_PROMPT = `你是一场海龟汤（情境推理谜题）的终审主持人。玩家会提交一份对汤底的还原，重点解释“汤面背后的真实经过与核心反转”。

你的任务：
1. 将玩家的汤底还原与真实汤底进行语义/逻辑对比。
2. 重点评估 processAccurate：玩家是否准确说出了核心反转、关键动作、连锁巧合或隐藏真相。这是决定 overallAccuracy 的核心。
3. culpritCorrect：如果汤面有明确责任人/凶手，玩家是否正确指认；如果没有责任人，只要玩家没有错误捏造，即可视为 true。
4. motiveConsistent：如果汤面有明确动机，玩家的动机理解是否一致；如果没有动机，只要玩家没有错误编造，即可视为 true。
5. 给出 overallAccuracy，范围 0.0 到 1.0，主要由 processAccurate 决定。
6. 给出面向玩家的 feedback（1-3 句话），像主持人一样点出还原在核心反转/关键经过上的偏差或缺失，但不要直接复述完整汤底或责任人姓名。
7. 给出内部 reason。

重要：
- 这个模式的重点是“还原汤底 / 核心反转”，不是猜责任人或动机。即使玩家没说出责任人/动机，只要核心反转还原到位，overallAccuracy 也可以很高。
- 即使玩家还原错误，也不能在 feedback 里直接写出完整汤底或责任人姓名。可以提示“关键触发动作还没说到”“核心反转还没抓住”“连锁反应的环节有遗漏”。

返回 ONLY JSON：
{
  "culpritCorrect": boolean,
  "motiveConsistent": boolean,
  "processAccurate": boolean,
  "overallAccuracy": number,
  "correctCulprit": "string (真实责任人姓名或'无责任人/意外'，仅在需要时提供，不要出现在 feedback 中)",
  "feedback": "string",
  "reason": "string"
}`;

function buildFactsBlock(caseObj) {
  return `汤底核心要素清单：\n${JSON.stringify(caseObj.facts, null, 2)}`;
}

function buildHistoryBlock(history) {
  if (!history || history.length === 0) {
    return '问答历史：无';
  }
  const lines = history.map((h, i) => {
    if (h.type === 'question') {
      return `${i + 1}. 玩家提问：“${h.input}” → 回答：${h.answer}`;
    }
    return `${i + 1}. 玩家提交汤底还原：“${h.input}” → 准确度：${h.accuracy ?? (h.correct ? '成功' : '未通过')}`;
  });
  return `问答历史：\n${lines.join('\n')}`;
}

function buildQuestionUserPrompt(caseObj, history, question) {
  return [
    buildFactsBlock(caseObj),
    '',
    '汤底（仅裁判可见，不得向玩家泄露）：',
    caseObj.fullStory,
    '',
    buildHistoryBlock(history),
    '',
    `玩家当前问题：“${question}”`,
    '',
    '请判断并返回 JSON。',
  ].join('\n');
}

function buildReconstructionUserPrompt(caseObj, history, reconstruction) {
  return [
    buildFactsBlock(caseObj),
    '',
    '汤底：',
    caseObj.fullStory,
    '',
    buildHistoryBlock(history),
    '',
    '玩家汤底还原：',
    reconstruction,
    '',
    '请评估并返回 JSON。',
  ].join('\n');
}

function collectSensitiveStrings(caseObj) {
  const facts = caseObj.facts || {};
  const list = [
    facts.culprit,
    facts.victim,
    facts.murderWeapon,
    facts.motive,
    ...(facts.keyClues || []),
    ...(facts.timeline || []),
  ].filter(Boolean);
  return [...new Set(list)].sort((a, b) => b.length - a.length);
}

function sanitizeCaseFeedback(feedback, caseObj) {
  if (!feedback || !caseObj) return feedback;
  const sensitive = collectSensitiveStrings(caseObj);
  let sanitized = feedback;
  for (const text of sensitive) {
    const escaped = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');
    sanitized = sanitized.replace(regex, '该信息');
  }
  return sanitized;
}

function validateQuestionResult(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid question response');
  }
  if (!VALID_ANSWERS.includes(obj.answer)) {
    throw new Error(`Invalid answer: ${obj.answer}`);
  }
  if (!obj.feedback || typeof obj.feedback !== 'string') {
    throw new Error('Missing or invalid "feedback" field');
  }
  return {
    answer: obj.answer,
    feedback: obj.feedback,
    reason: obj.reason || '',
  };
}

function validateReconstructionResult(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid reconstruction response');
  }
  if (typeof obj.culpritCorrect !== 'boolean') {
    throw new Error('Missing or invalid "culpritCorrect" field');
  }
  if (typeof obj.motiveConsistent !== 'boolean') {
    throw new Error('Missing or invalid "motiveConsistent" field');
  }
  if (typeof obj.processAccurate !== 'boolean') {
    throw new Error('Missing or invalid "processAccurate" field');
  }
  const accuracy = Number(obj.overallAccuracy);
  if (Number.isNaN(accuracy) || accuracy < 0 || accuracy > 1) {
    throw new Error(`Invalid overallAccuracy: ${obj.overallAccuracy}`);
  }
  if (!obj.feedback || typeof obj.feedback !== 'string') {
    throw new Error('Missing or invalid "feedback" field');
  }
  return {
    culpritCorrect: obj.culpritCorrect,
    motiveConsistent: obj.motiveConsistent,
    processAccurate: obj.processAccurate,
    overallAccuracy: accuracy,
    correctCulprit: obj.correctCulprit || '',
    feedback: obj.feedback,
    reason: obj.reason || '',
  };
}

function accuracyToConfidence(accuracy) {
  if (accuracy >= 0.85) return 'certain';
  if (accuracy >= 0.6) return 'likely';
  if (accuracy >= 0.3) return 'possible';
  return 'wrong';
}

export async function answerCaseQuestion(client, { case: caseObj, history, question }) {
  const messages = [
    { role: 'system', content: QUESTION_SYSTEM_PROMPT },
    { role: 'user', content: buildQuestionUserPrompt(caseObj, history, question) },
  ];

  const response = await client.send(messages, {
    responseFormat: { type: 'json_object' },
  });

  const parsed = extractJson(response);
  const result = validateQuestionResult(parsed);
  return {
    type: 'question',
    input: question,
    answer: result.answer,
    feedback: sanitizeCaseFeedback(result.feedback, caseObj),
    reason: result.reason,
  };
}

export async function verifyReconstruction(client, { case: caseObj, history, reconstruction }) {
  const messages = [
    { role: 'system', content: RECONSTRUCTION_SYSTEM_PROMPT },
    { role: 'user', content: buildReconstructionUserPrompt(caseObj, history, reconstruction) },
  ];

  const response = await client.send(messages, {
    responseFormat: { type: 'json_object' },
  });

  const parsed = extractJson(response);
  const result = validateReconstructionResult(parsed);
  const correct = result.overallAccuracy >= WIN_ACCURACY_THRESHOLD;
  const feedback = sanitizeCaseFeedback(result.feedback, caseObj);

  return {
    type: 'guess',
    input: reconstruction,
    correct,
    accuracy: result.overallAccuracy,
    matchConfidence: accuracyToConfidence(result.overallAccuracy),
    culpritCorrect: result.culpritCorrect,
    motiveConsistent: result.motiveConsistent,
    processAccurate: result.processAccurate,
    feedback,
    reason: result.reason,
  };
}
